import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Um ponto da série diária (ex.: get_agencia_carga_diaria). `dia` é data lógica "YYYY-MM-DD". */
export interface CounterPonto {
  dia: string;
  valor: number;
}

interface TimelineCounterLaneProps {
  label: string;
  windowFromMs: number;
  windowToMs: number;
  nowPercent: number;
  pontos: CounterPonto[];
  /** Rótulo do que está sendo contado, usado no tooltip (ex.: "freelancers ativos"). */
  unidadeLabel: string;
  formatarDia: (dia: string) => string;
}

const VIEWBOX_LARGURA = 1000;
const VIEWBOX_ALTURA = 24;
const TOPO = 2;
const BASE = VIEWBOX_ALTURA - 2;
// Fator de folga: o pico da série fica a 85% da altura útil, nunca encostando no topo.
const FATOR_FOLGA = 0.85;

function clampPercent(percent: number) {
  return Math.min(Math.max(percent, 0), 100);
}

/**
 * Lane de CONTADOR (§3.3 do plano da agência): gráfico de degraus (step chart)
 * desenhado à mão em SVG, sem lib nova — mesmo espírito do
 * DisponibilidadeTimeline. Alinhado ao MESMO eixo de tempo (windowFromMs/
 * windowToMs) das lanes de estado/evento, para que o degrau de "entrou em
 * projeto" bata visualmente com o marco correspondente nas lanes abaixo.
 * Interação: pontos de valor igual são agrupados num único trecho com
 * tooltip (evita 1 tooltip por dia numa janela de 24 meses).
 */
export function TimelineCounterLane({
  label,
  windowFromMs,
  windowToMs,
  nowPercent,
  pontos,
  unidadeLabel,
  formatarDia,
}: TimelineCounterLaneProps) {
  const totalMs = Math.max(windowToMs - windowFromMs, 1);
  const positionPercent = (ms: number) => clampPercent(((ms - windowFromMs) / totalMs) * 100);

  const ordenados = useMemo(
    () =>
      [...pontos]
        .filter((ponto) => !Number.isNaN(new Date(ponto.dia).getTime()))
        .sort((pontoA, pontoB) => new Date(pontoA.dia).getTime() - new Date(pontoB.dia).getTime()),
    [pontos],
  );

  const maiorValor = Math.max(1, ...ordenados.map((ponto) => ponto.valor));

  const posicaoX = (dia: string) => (positionPercent(new Date(dia).getTime()) / 100) * VIEWBOX_LARGURA;
  const posicaoY = (valor: number) => BASE - (valor / maiorValor) * (BASE - TOPO) * FATOR_FOLGA;

  const xFim = (positionPercent(windowToMs) / 100) * VIEWBOX_LARGURA;

  // Caminho de degraus (step-after): mantém o valor constante até o próximo ponto.
  const { caminhoLinha, caminhoArea } = useMemo(() => {
    if (!ordenados.length) return { caminhoLinha: "", caminhoArea: "" };
    const primeiro = ordenados[0];
    let linha = `M ${posicaoX(primeiro.dia).toFixed(2)},${posicaoY(primeiro.valor).toFixed(2)}`;
    for (let indice = 1; indice < ordenados.length; indice += 1) {
      const pontoAtual = ordenados[indice];
      const x = posicaoX(pontoAtual.dia).toFixed(2);
      linha += ` H ${x} V ${posicaoY(pontoAtual.valor).toFixed(2)}`;
    }
    linha += ` H ${xFim.toFixed(2)}`;
    const area = `${linha} L ${xFim.toFixed(2)},${BASE} L ${posicaoX(primeiro.dia).toFixed(2)},${BASE} Z`;
    return { caminhoLinha: linha, caminhoArea: area };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenados, windowFromMs, windowToMs]);

  // Agrupa trechos de valor constante num único hit-target de tooltip (em vez
  // de um por dia — a janela pode ter ~730 pontos).
  const trechos = useMemo(() => {
    if (!ordenados.length) return [];
    const grupos: { inicio: string; fim: string; valor: number }[] = [];
    for (const ponto of ordenados) {
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.valor === ponto.valor) {
        ultimo.fim = ponto.dia;
      } else {
        grupos.push({ inicio: ponto.dia, fim: ponto.dia, valor: ponto.valor });
      }
    }
    return grupos;
  }, [ordenados]);

  return (
    <div className="flex items-center gap-2">
      <span className="w-[104px] shrink-0 text-[11px] text-muted-foreground text-right">
        {label}
      </span>
      <div className="relative h-6 flex-1 rounded bg-slate-50">
        {ordenados.length > 0 && (
          <svg
            viewBox={`0 0 ${VIEWBOX_LARGURA} ${VIEWBOX_ALTURA}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full text-blue-600"
            aria-hidden
          >
            <path d={caminhoArea} fill="currentColor" opacity={0.1} stroke="none" />
            <path
              d={caminhoLinha}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}

        {/* Hit-targets por trecho de valor constante (tooltip "por dia" sem 1 nó por dia) */}
        {trechos.map((trecho, indice) => {
          const inicioMs = new Date(trecho.inicio).getTime();
          const fimMs = Math.min(
            new Date(trecho.fim).getTime() + 24 * 60 * 60 * 1000,
            windowToMs,
          );
          const leftPercent = positionPercent(inicioMs);
          const widthPercent = Math.max(positionPercent(fimMs) - leftPercent, 0.4);
          const periodoTexto =
            trecho.inicio === trecho.fim
              ? formatarDia(trecho.inicio)
              : `de ${formatarDia(trecho.inicio)} até ${formatarDia(trecho.fim)}`;
          return (
            <Tooltip key={indice}>
              <TooltipTrigger asChild>
                <div
                  className="absolute inset-y-0"
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                <div className="space-y-0.5 text-xs">
                  <div className="font-medium">
                    {trecho.valor} {unidadeLabel}
                  </div>
                  <div className="text-muted-foreground">{periodoTexto}</div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Marcador "hoje", mesmo estilo das demais lanes */}
        <div
          className="absolute top-0 h-full w-px bg-foreground/40"
          style={{ left: `${clampPercent(nowPercent)}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

export default TimelineCounterLane;
